import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { ShoppingBag, Search, Globe, ChevronDown, ChevronRight, ArrowRight, User, Heart, Menu, X, Gift, Home, LayoutGrid, Users, Sparkles, Info, Truck, Package } from "lucide-react";
import CategoryMegaMenu from "./CategoryMegaMenu";
import NotificationBell from "./NotificationBell";
import { listCategoriesTree, listAll } from "../services/firestore";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

const LANGS = [{ k: "az", l: "AZ" }, { k: "en", l: "EN" }, { k: "ru", l: "RU" }];
const CURRS = ["AZN", "USD", "EUR", "RUB"];

export default function Header() {
  const { t, lang, setLang, currency, setCurrency, content, formatPrice } = useApp();
  const { count, setOpen } = useCart();
  const { user, isAdmin, logout, profile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileTree, setMobileTree] = useState([]);
  const [expandedCats, setExpandedCats] = useState({}); // cat id → bool
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [allProducts, setAllProducts] = useState([]);
  const [allArtists, setAllArtists] = useState([]);
  const closeTimer = useRef(null);
  const searchInputRef = useRef(null);

  // Determine if we should use "transparent over hero" style:
  // only on home page, when there's at least one banner with media, and not scrolled.
  const hasHeroMedia = Array.isArray(content?.banners) &&
    content.banners.some((b) => b?.video_url || b?.video_url_mobile || b?.image_url);
  const isHome = location.pathname === "/";
  const overHero = isHome && hasHeroMedia && !scrolled && !mobileOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openMega = () => { clearTimeout(closeTimer.current); setMegaOpen(true); };
  const closeMegaSoon = () => { closeTimer.current = setTimeout(() => setMegaOpen(false), 120); };
  const closeMobile = () => {
    setMobileOpen(false);
    // Reset accordion state and scroll the drawer back to top so
    // the next time it opens it looks fresh.
    setExpandedCats({});
  };
  const toggleCat = (id) => setExpandedCats((s) => ({ ...s, [id]: !s[id] }));

  // Locale-aware uppercase that preserves Azerbaijani/Turkish "İ" (dotted capital I).
  // Default JS toUpperCase on "i" → "I" (no dot). We use az-AZ which yields "İ".
  const upperLocale = lang === "ru" ? "ru-RU" : lang === "en" ? "en-US" : "az-AZ";
  const upper = (s) => (s == null ? "" : String(s).toLocaleUpperCase(upperLocale));

  // Title-case helper for mobile menu labels (handles strings that come pre-uppercased
  // from translations like "DİL" / "VALYUTA" — converts to "Dil" / "Valyuta",
  // and leaves natural-case strings unchanged, all locale-aware).
  const titleCase = (s) => {
    if (s == null) return "";
    const lower = String(s).toLocaleLowerCase(upperLocale);
    return lower.replace(/(^|\s|-|\/)\S/g, (m) => m.toLocaleUpperCase(upperLocale));
  };

  // Lazy-load categories when mobile drawer opens
  useEffect(() => {
    if (mobileOpen && mobileTree.length === 0) {
      listCategoriesTree().then(setMobileTree).catch(() => setMobileTree([]));
    }
  }, [mobileOpen, mobileTree.length]);

  // Lazy-load products/artists once when search is first opened
  useEffect(() => {
    if (searchOpen && allProducts.length === 0) {
      Promise.all([listAll("products"), listAll("artists")])
        .then(([p, a]) => { setAllProducts(p || []); setAllArtists(a || []); })
        .catch(() => { /* ignore */ });
    }
  }, [searchOpen, allProducts.length]);

  // Focus input + Esc to close + close on route change
  useEffect(() => {
    if (!searchOpen) return;
    setTimeout(() => searchInputRef.current?.focus(), 50);
    const onKey = (e) => { if (e.key === "Escape") setSearchOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [searchOpen]);

  useEffect(() => { setSearchOpen(false); }, [location.pathname]);

  const artistById = useMemo(
    () => Object.fromEntries(allArtists.map((a) => [a.id, a.name])),
    [allArtists]
  );

  const searchResults = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return [];
    return allProducts
      .filter((p) => {
        const a = (artistById[p.artist_id] || "").toLowerCase();
        return (
          (p.title || "").toLowerCase().includes(q) ||
          (p.catalog_no || "").toLowerCase().includes(q) ||
          a.includes(q)
        );
      })
      .slice(0, 24);
  }, [searchQ, allProducts, artistById]);

  const navItems = [
    { to: "/", label: t.nav.home, end: true, icon: Home },
    { to: "/artists", label: t.nav.artists, icon: Users },
    { to: "/gift-cards", label: t.nav.giftCards || "Hədiyyə Kartı", icon: Gift, accent: true },
    { to: "/wheel", label: "Şans Çarxı", icon: Sparkles, accent: true },
    { to: "/blog", label: t.footer.companyLinks?.blog || "Yeniliklər", icon: Sparkles },
    { to: "/about", label: t.footer.companyLinks?.about || "Haqqımızda", icon: Info },
  ];

  return (
    <header
      className={`sticky top-0 z-[60] transition-colors duration-300 ${overHero ? "glass-header-over" : "glass-header"}`}
      data-over-hero={overHero ? "1" : "0"}
      data-testid="site-header"
    >
      {/* TOP STRIP — minimal, only lang/currency */}
      <div className="header-top-strip border-b border-[var(--border-soft)] hidden md:block">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 flex items-center justify-end gap-6 py-2">
          <DropdownMenu>
            <DropdownMenuTrigger className="metadata text-[11px] flex items-center gap-1.5 hover:text-[var(--primary)] transition-colors">
              <Globe size={13} /> {lang.toUpperCase()} <ChevronDown size={11} />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[var(--bg)] border-[var(--border)] rounded-none min-w-[80px]">
              {LANGS.map((l) => (
                <DropdownMenuItem key={l.k} className="metadata text-[11px] cursor-pointer" onClick={() => setLang(l.k)}>
                  {l.l}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger className="metadata text-[11px] flex items-center gap-1.5 hover:text-[var(--primary)] transition-colors">
              {currency} <ChevronDown size={11} />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[var(--bg)] border-[var(--border)] rounded-none min-w-[80px]">
              {CURRS.map((c) => (
                <DropdownMenuItem key={c} className="metadata text-[11px] cursor-pointer" onClick={() => setCurrency(c)}>
                  {c}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* MAIN ROW */}
      <div className="max-w-screen-2xl mx-auto px-5 md:px-12 flex items-center justify-between h-16 md:h-20 relative">
        {/* MOBILE: hamburger / close + profile icon */}
        <div className="md:hidden flex items-center -ml-2 relative z-[60]">
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="flex items-center justify-center w-10 h-10 text-[var(--text)] hover:text-[var(--primary)] transition-colors"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-trigger"
          >
            {mobileOpen ? <X size={22} strokeWidth={1.6} /> : <Menu size={22} strokeWidth={1.6} />}
          </button>
          <Link
            to={user ? "/profile" : "/login"}
            className="flex items-center justify-center w-10 h-10 text-[var(--text)] hover:text-[var(--primary)] transition-colors"
            aria-label={user ? t.common.profile : t.common.login}
            data-testid="mobile-profile-trigger"
          >
            <User size={20} strokeWidth={1.6} />
          </Link>
        </div>

        {/* DESKTOP NAV — LEFT half of the row (before centered logo) */}
        <nav className="hidden md:flex items-center gap-6 lg:gap-8 flex-1 justify-start">
          <NavLink to="/" end className={({isActive}) => `nav-link link-underline ${isActive ? "text-[var(--text)]" : "text-[var(--text-2)]"}`} data-testid="nav-home">{t.nav.home}</NavLink>
          <button
            onMouseEnter={openMega}
            onMouseLeave={closeMegaSoon}
            onClick={() => setMegaOpen((v) => !v)}
            className={`nav-link link-underline flex items-center gap-1 ${megaOpen ? "text-[var(--text)]" : "text-[var(--text-2)]"}`}
            data-testid="nav-catalog"
          >
            {t.nav.catalog} <ChevronDown size={12} className={`transition-transform ${megaOpen ? "rotate-180" : ""}`} />
          </button>
          <NavLink to="/artists" className={({isActive}) => `nav-link link-underline ${isActive ? "text-[var(--text)]" : "text-[var(--text-2)]"}`} data-testid="nav-artists">{t.nav.artists}</NavLink>
          <NavLink to="/gift-cards" className={({isActive}) => `nav-link link-underline flex items-center gap-1.5 ${isActive ? "text-[var(--primary)]" : "text-[var(--text-2)] hover:text-[var(--primary)]"}`} data-testid="nav-gift-cards">
            <Gift size={13} /> {t.nav.giftCards || "Hədiyyə Kartı"}
          </NavLink>
        </nav>

        {/* LOGO — always absolute-centered on both mobile and desktop */}
        <Link
          to="/"
          data-testid="logo"
          className="flex-shrink-0 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[1] flex items-center"
          aria-label="Vinyl Baku"
        >
          <img
            src="/vinyl-baku-logo.png"
            alt="Vinyl Baku"
            width="120"
            height="60"
            fetchPriority="high"
            className="h-8 md:h-10 w-auto object-contain site-logo-img"
          />
        </Link>

        {/* DESKTOP NAV — RIGHT half of the row (after centered logo) */}
        <nav className="hidden md:flex items-center gap-6 lg:gap-8 flex-1 justify-end md:pr-6 lg:pr-8">
          <NavLink to="/wheel" className={({isActive}) => `nav-link link-underline flex items-center gap-1.5 ${isActive ? "text-[var(--primary)]" : "text-[var(--text-2)] hover:text-[var(--primary)]"}`} data-testid="nav-wheel">
            <Sparkles size={13} /> Şans Çarxı
          </NavLink>
          <NavLink to="/blog" className={({isActive}) => `nav-link link-underline ${isActive ? "text-[var(--text)]" : "text-[var(--text-2)]"}`} data-testid="nav-blog">{t.footer.companyLinks?.blog || "Yeniliklər"}</NavLink>
          <NavLink to="/about" className={({isActive}) => `nav-link link-underline ${isActive ? "text-[var(--text)]" : "text-[var(--text-2)]"}`} data-testid="nav-about">{t.footer.companyLinks?.about || "Haqqımızda"}</NavLink>
        </nav>

        {/* RIGHT ACTIONS */}
        <div className="flex items-center gap-1 md:gap-5 md:pl-2">
          <button
            type="button"
            onClick={() => setSearchOpen((v) => !v)}
            className="flex items-center justify-end pr-1 w-9 h-9 md:w-auto md:h-auto md:pr-0 text-[var(--text-2)] hover:text-[var(--primary)] transition-colors"
            data-testid="search-trigger"
            aria-label={t.common.searchShort}
            aria-expanded={searchOpen}
          >
            <Search size={17} />
          </button>

          {/* Notifications — visible on all viewports when logged in (component returns null otherwise) */}
          <NotificationBell />

          {/* Wishlist / Favorites — always visible; redirects to login if not authed */}
          <Link
            to={user ? "/favorites" : "/login?next=/favorites"}
            className="flex items-center justify-center w-9 h-9 md:w-auto md:h-auto text-[var(--text-2)] hover:text-[var(--accent)] transition-colors"
            data-testid="nav-favorites"
            aria-label={t.fav.title}
          >
            <Heart size={17} />
          </Link>

          {!user ? (
            <Link to="/login" className="hidden md:flex items-center gap-1.5 nav-link hover:text-[var(--primary)]" data-testid="login-link">
              <User size={15} /> <span>{t.common.login}</span>
            </Link>
          ) : (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger className="hidden md:flex items-center gap-1.5 nav-link hover:text-[var(--primary)]" data-testid="user-trigger">
                  <User size={15} />
                  <span>{(profile?.full_name || user.email).split(" ")[0]}</span>
                  <ChevronDown size={12} />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white border border-black/10 rounded-md min-w-[220px] p-2 shadow-xl">
                  <div className="px-3 py-2 mb-1 border-b border-black/5">
                    <div className="text-[10px] tracking-[0.18em] uppercase text-black/40 font-medium">{t.common.account}</div>
                    <div className="text-sm text-[var(--text)] font-medium truncate mt-0.5">{profile?.full_name || user.email}</div>
                  </div>
                  <DropdownMenuItem asChild className="user-menu-item cursor-pointer">
                    <Link to="/profile" data-testid="menu-profile">{titleCase(t.common.profile)}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="user-menu-item cursor-pointer">
                    <Link to="/orders" data-testid="menu-orders">{titleCase(t.profile.myOrders)}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="user-menu-item cursor-pointer">
                    <Link to="/favorites" data-testid="menu-favorites">{titleCase(t.fav.title)}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="user-menu-item cursor-pointer">
                    <Link to="/my-gift-cards" data-testid="menu-my-gift-cards">{titleCase(t.nav.myGiftCards || "Hədiyyə Kartlarım")}</Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild className="user-menu-item cursor-pointer text-[var(--accent)]">
                      <Link to="/admin" data-testid="menu-admin">{titleCase(t.nav.admin)}</Link>
                    </DropdownMenuItem>
                  )}
                  <div className="my-1 h-px bg-black/5" />
                  <DropdownMenuItem className="user-menu-item cursor-pointer text-black/55 hover:text-[var(--accent)]" onClick={logout} data-testid="menu-logout">{titleCase(t.common.logout)}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}

          <button
            onClick={() => setOpen(true)}
            className="flex items-center justify-center w-9 h-9 md:w-auto md:h-auto md:gap-1.5 nav-link text-[var(--text-2)] hover:text-[var(--primary)] relative transition-colors"
            data-testid="cart-button"
            aria-label={t.common.cart}
          >
            <ShoppingBag size={17} />
            <span className="hidden md:inline">{t.common.cart}</span>
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 md:static md:ml-2 min-w-[20px] h-[20px] px-1.5 inline-flex items-center justify-center bg-[var(--accent)] text-white rounded-full text-[10px] font-medium leading-none">
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* SEARCH DROPDOWN — opens in-place without navigation */}
      {searchOpen && (
        <>
          <div
            className="fixed inset-0 z-[55] bg-black/30"
            onClick={() => setSearchOpen(false)}
            data-testid="search-backdrop"
          />
          <div
            className="absolute left-0 right-0 top-full z-[57] bg-white border-t border-b border-black/10 shadow-2xl search-panel-anim"
            data-testid="search-panel"
          >
            <div className="max-w-screen-2xl mx-auto px-5 md:px-12 py-5">
              <form
                onSubmit={(e) => { e.preventDefault(); /* No page navigation on Enter */ }}
                className="flex items-center gap-3 border-b border-black/10 pb-3"
              >
                <Search size={18} className="text-black/45 shrink-0" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  placeholder={`${t.common.searchShort || "Axtarış"}…`}
                  className="flex-1 bg-transparent outline-none text-base md:text-lg placeholder-black/35 text-black"
                  data-testid="search-input"
                />
                {searchQ && (
                  <button
                    type="button"
                    onClick={() => setSearchQ("")}
                    className="text-black/40 hover:text-black w-8 h-8 flex items-center justify-center"
                    aria-label="Sıfırla"
                    data-testid="search-clear"
                  >
                    <X size={16} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setSearchOpen(false)}
                  className="metadata text-[10px] tracking-[0.18em] uppercase text-black/45 hover:text-black px-2 hidden md:inline"
                  data-testid="search-close"
                >
                  ESC
                </button>
              </form>

              {/* Results */}
              <div className="mt-4 max-h-[60vh] overflow-y-auto">
                {searchQ.trim() === "" && (
                  <div className="py-8 text-center text-black/40 text-sm">
                    <div className="metadata">AXTARIŞA BAŞLAYIN</div>
                    <div className="text-xs mt-2">Albom adı, sənətçi və ya barkod yazın</div>
                  </div>
                )}

                {searchQ.trim() !== "" && searchResults.length === 0 && (
                  <div className="py-8 text-center text-black/45 text-sm" data-testid="search-empty">
                    <div className="metadata">NƏTİCƏ TAPILMADI</div>
                    <div className="text-xs mt-2">Başqa açar söz cəhd edin</div>
                  </div>
                )}

                {searchResults.length > 0 && (
                  <div className="text-[10px] tracking-[0.18em] uppercase text-black/35 pt-2 pb-2">
                    {searchResults.length} NƏTİCƏ
                  </div>
                )}

                {searchResults.length > 0 && (
                  <ul className="divide-y divide-black/5">
                    {searchResults.map((p) => {
                      const discountPct = Math.max(0, Math.min(90, Number(p.discount_pct) || 0));
                      const hasDiscount = discountPct > 0;
                      const finalPrice = hasDiscount
                        ? Number(p.price_azn) * (1 - discountPct / 100)
                        : Number(p.price_azn);
                      return (
                        <li key={p.id}>
                          <Link
                            to={`/product/${p.slug}`}
                            onClick={() => setSearchOpen(false)}
                            className="flex items-center gap-3 py-3 group hover:bg-black/[0.02] -mx-2 px-2 rounded-md transition-colors"
                            data-testid={`search-result-${p.slug}`}
                          >
                            <div className="w-12 h-12 md:w-14 md:h-14 shrink-0 overflow-hidden rounded bg-zinc-100">
                              {p.cover_url ? (
                                <img src={p.cover_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-black/30 font-serif">V</div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-[10px] tracking-[0.16em] uppercase text-black/40 truncate">{artistById[p.artist_id] || ""}</div>
                              <div className="text-sm md:text-base font-medium text-black truncate group-hover:text-[var(--primary)] transition-colors">{p.title}</div>
                            </div>
                            <div className="text-right shrink-0 font-mono text-xs md:text-sm">
                              {hasDiscount ? (
                                <>
                                  <div className="line-through text-black/35">{formatPrice(p.price_azn)}</div>
                                  <div className="text-[var(--accent)] font-medium">{formatPrice(finalPrice)}</div>
                                </>
                              ) : (
                                <div className="text-black">{formatPrice(p.price_azn)}</div>
                              )}
                            </div>
                            <ArrowRight size={14} className="text-black/30 group-hover:text-black transition-colors shrink-0 hidden md:block" strokeWidth={1.5} />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* MEGA MENU (desktop only) */}
      <div onMouseEnter={openMega} onMouseLeave={closeMegaSoon} className="hidden md:block">
        <CategoryMegaMenu open={megaOpen} onClose={() => setMegaOpen(false)} label={t.nav.catalog} />
      </div>

      {/* MOBILE DRAWER */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild><span className="hidden" /></SheetTrigger>
        <SheetContent
          side="left"
          hideClose
          className="w-screen sm:w-[420px] bg-white border-r border-black/10 p-0 flex flex-col"
          data-testid="mobile-drawer"
        >
          {/* Drawer header — "Menu" eyebrow left, X close right */}
          <div className="m-drawer-header">
            <span className="m-drawer-header__title">Menu</span>
            <button
              onClick={closeMobile}
              className="m-drawer-header__close"
              aria-label="Close"
              data-testid="mobile-drawer-close"
              type="button"
            >
              <X size={20} strokeWidth={1.5} />
            </button>
          </div>

          {/* Nav links */}
          <nav className="flex-1 overflow-y-auto pt-3 pb-8">
            <ul className="flex flex-col">
              {/* Kateqoriyalar — expandable, no icon */}
              <li>
                <button
                  onClick={() => toggleCat("__catalog__")}
                  className={`m-nav-row ${expandedCats.__catalog__ ? "is-active" : ""}`}
                  data-testid="mobile-nav-catalog"
                  type="button"
                >
                  <span>{upper(t.nav.catalog)}</span>
                  <ChevronDown
                    className={`m-nav-row__chev transition-transform ${expandedCats.__catalog__ ? "rotate-180" : ""}`}
                    strokeWidth={1.5}
                  />
                </button>
                {expandedCats.__catalog__ && (
                  <ul className="pb-3 fade-up">
                    {mobileTree.map((c1) => renderMobileCategoryNode({
                      node: c1,
                      depth: 0,
                      expandedCats,
                      toggleCat,
                      closeMobile,
                      upper,
                    }))}
                    {mobileTree.length === 0 && (
                      <li className="m-sub-row text-black/35">…</li>
                    )}
                  </ul>
                )}
              </li>

              {/* Sənətçilər, Yeniliklər, Haqqımızda, Sifarişi izlə — no icon */}
              {navItems.filter((n) => n.to !== "/" && !n.accent).map((n) => (
                <li key={n.to}>
                  <NavLink
                    to={n.to}
                    onClick={closeMobile}
                    className={({ isActive }) => `m-nav-row ${isActive ? "is-active" : ""}`}
                    data-testid={`mobile-nav-${n.to.replace("/", "") || "home"}`}
                  >
                    <span>{upper(n.label)}</span>
                    <ChevronRight className="m-nav-row__chev" strokeWidth={1.5} />
                  </NavLink>
                </li>
              ))}

              {/* Hədiyyə Kartı — accent row with gift icon + arrow */}
              {navItems.filter((n) => n.accent).map((n) => (
                <li key={n.to}>
                  <NavLink
                    to={n.to}
                    onClick={closeMobile}
                    className={({ isActive }) => `m-nav-row is-accent ${isActive ? "is-active" : ""}`}
                    data-testid={`mobile-nav-${n.to.replace("/", "") || "home"}`}
                  >
                    {n.icon && <n.icon className="m-nav-row__icon" strokeWidth={1.5} />}
                    <span>{upper(n.label)}</span>
                    <ArrowRight className="m-nav-row__chev" strokeWidth={1.5} />
                  </NavLink>
                </li>
              ))}

              {/* DİL + VALYUTA — yan-yana iki bölmə tək sətirdə */}
              <li>
                <div className="m-pref-row">
                  <button
                    onClick={() => toggleCat("__lang__")}
                    className={`m-pref-cell ${expandedCats.__lang__ ? "is-open" : ""}`}
                    type="button"
                    data-testid="mobile-nav-lang"
                  >
                    <span className="m-pref-label">{upper(t.common.language || "Dil")}</span>
                    <span className="m-pref-value">
                      {lang.toUpperCase()}
                      <ChevronDown
                        size={13}
                        strokeWidth={1.6}
                        className={`m-pref-chev transition-transform ${expandedCats.__lang__ ? "rotate-180" : ""}`}
                      />
                    </span>
                  </button>
                  <div className="m-pref-divider" />
                  <button
                    onClick={() => toggleCat("__currency__")}
                    className={`m-pref-cell ${expandedCats.__currency__ ? "is-open" : ""}`}
                    type="button"
                    data-testid="mobile-nav-currency"
                  >
                    <span className="m-pref-label">{upper(t.common.currency || "Valyuta")}</span>
                    <span className="m-pref-value">
                      {currency}
                      <ChevronDown
                        size={13}
                        strokeWidth={1.6}
                        className={`m-pref-chev transition-transform ${expandedCats.__currency__ ? "rotate-180" : ""}`}
                      />
                    </span>
                  </button>
                </div>

                {expandedCats.__lang__ && (
                  <div className="px-6 pb-5 pt-3 fade-up border-b border-black/5">
                    <div className="flex items-center gap-2 flex-wrap">
                      {LANGS.map((l) => (
                        <button
                          key={l.k}
                          onClick={() => setLang(l.k)}
                          className={`text-[11px] tracking-[0.18em] uppercase px-4 py-2 border rounded-full transition-colors ${
                            lang === l.k
                              ? "border-black bg-black text-white"
                              : "border-black/15 text-black/55 hover:border-black/40 hover:text-black"
                          }`}
                          type="button"
                        >
                          {l.l}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {expandedCats.__currency__ && (
                  <div className="px-6 pb-5 pt-3 fade-up border-b border-black/5">
                    <div className="flex items-center gap-2 flex-wrap">
                      {CURRS.map((c) => (
                        <button
                          key={c}
                          onClick={() => setCurrency(c)}
                          className={`text-[11px] tracking-[0.18em] uppercase px-4 py-2 border rounded-full transition-colors ${
                            currency === c
                              ? "border-black bg-black text-white"
                              : "border-black/15 text-black/55 hover:border-black/40 hover:text-black"
                          }`}
                          type="button"
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </li>

              {/* AUTH row */}
              {!user ? (
                <li>
                  <Link
                    to="/login"
                    onClick={closeMobile}
                    className="m-nav-row"
                    data-testid="mobile-login-btn"
                  >
                    <User className="m-nav-row__icon" strokeWidth={1.5} />
                    <span>{upper(t.common.login)}</span>
                    <ChevronRight className="m-nav-row__chev" strokeWidth={1.5} />
                  </Link>
                </li>
              ) : (
                <>
                  <li>
                    <Link to="/profile" onClick={closeMobile} className="m-nav-row">
                      <User className="m-nav-row__icon" strokeWidth={1.5} />
                      <span>{upper(t.common.profile)}</span>
                      <ChevronRight className="m-nav-row__chev" strokeWidth={1.5} />
                    </Link>
                  </li>
                  <li>
                    <Link to="/orders" onClick={closeMobile} className="m-nav-row" data-testid="mobile-nav-orders">
                      <Package className="m-nav-row__icon" strokeWidth={1.5} />
                      <span>{upper(t.profile.myOrders)}</span>
                      <ChevronRight className="m-nav-row__chev" strokeWidth={1.5} />
                    </Link>
                  </li>
                  <li>
                    <Link to="/favorites" onClick={closeMobile} className="m-nav-row">
                      <Heart className="m-nav-row__icon" strokeWidth={1.5} />
                      <span>{upper(t.fav.title)}</span>
                      <ChevronRight className="m-nav-row__chev" strokeWidth={1.5} />
                    </Link>
                  </li>
                  <li>
                    <Link to="/my-gift-cards" onClick={closeMobile} className="m-nav-row">
                      <Gift className="m-nav-row__icon" strokeWidth={1.5} />
                      <span>{upper(t.nav.myGiftCards || "Hədiyyə Kartlarım")}</span>
                      <ChevronRight className="m-nav-row__chev" strokeWidth={1.5} />
                    </Link>
                  </li>
                  {isAdmin && (
                    <li>
                      <Link to="/admin" onClick={closeMobile} className="m-nav-row is-accent">
                        <Sparkles className="m-nav-row__icon" strokeWidth={1.5} />
                        <span>{upper(t.nav.admin)}</span>
                        <ChevronRight className="m-nav-row__chev" strokeWidth={1.5} />
                      </Link>
                    </li>
                  )}
                  <li>
                    <button onClick={() => { logout(); closeMobile(); }} className="m-nav-row" type="button">
                      <X className="m-nav-row__icon" strokeWidth={1.5} />
                      <span>{upper(t.common.logout)}</span>
                    </button>
                  </li>
                </>
              )}
            </ul>
          </nav>
        </SheetContent>
      </Sheet>
    </header>
  );
}

/**
 * Recursive mobile drawer category renderer — supports arbitrary tree depth.
 * • Nodes WITH children: tapping the row toggles the sub-tree (no navigation).
 *   The chevron on the right ALSO toggles.
 * • Leaf nodes: tapping the row navigates to /catalog?category=<slug>.
 * • A small "Bütün ..." link at the top of each expanded sub-tree lets users
 *   still open the parent category page if they explicitly want to.
 */
function renderMobileCategoryNode({ node, depth, expandedCats, toggleCat, closeMobile, upper }) {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = !!expandedCats[node.id];
  const paddingLeft = 24 + depth * 18; // 24px base + 18px per level
  const depthClass = depth === 0 ? "" : depth === 1 ? "depth-3" : "depth-4";

  const rowInner = (
    <span className="flex-1 flex items-center justify-between">
      <span>{upper(node.name)}</span>
      {hasChildren && (
        <ChevronDown
          size={12}
          strokeWidth={1.5}
          className={`transition-transform text-black/45 mr-4 ${isExpanded ? "rotate-180" : ""}`}
        />
      )}
    </span>
  );

  return (
    <li key={node.id}>
      {hasChildren ? (
        <button
          type="button"
          onClick={() => toggleCat(node.id)}
          className={`m-sub-row w-full text-left hover:!text-[var(--accent)] ${depthClass}`}
          style={depth > 0 ? { paddingLeft: `${paddingLeft}px` } : undefined}
          data-testid={`mob-cat-${node.slug}`}
          aria-expanded={isExpanded}
        >
          {rowInner}
        </button>
      ) : (
        <Link
          to={`/catalog?category=${encodeURIComponent(node.slug)}`}
          onClick={closeMobile}
          className={`m-sub-row w-full hover:!text-[var(--accent)] ${depthClass}`}
          style={depth > 0 ? { paddingLeft: `${paddingLeft}px` } : undefined}
          data-testid={`mob-cat-${node.slug}`}
        >
          {rowInner}
        </Link>
      )}
      {hasChildren && isExpanded && (
        <ul className="pb-1 fade-up">
          <li>
            <Link
              to={`/catalog?category=${encodeURIComponent(node.slug)}`}
              onClick={closeMobile}
              className={`m-sub-row text-[var(--accent)]/80 italic ${depthClass}`}
              style={{ paddingLeft: `${paddingLeft + 18}px`, fontSize: "12px" }}
              data-testid={`mob-cat-all-${node.slug}`}
            >
              → {upper(node.name)} — hamısına bax
            </Link>
          </li>
          {node.children.map((child) => renderMobileCategoryNode({
            node: child,
            depth: depth + 1,
            expandedCats,
            toggleCat,
            closeMobile,
            upper,
          }))}
        </ul>
      )}
    </li>
  );
}
